import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Self-service password rotation card. Used by both super-admin (SettingsManager)
// and photographers (MySettings). Sends {current_password, new_password} to
// /api/auth/change-password. Server enforces 12-char minimum + current-password
// verification + per-(email,ip) lockout.
export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 12) {
      toast.error("New password must be at least 12 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    if (next === current) {
      toast.error("New password must differ from current password");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Log in again on any other device.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border-2 border-white/10 p-5 space-y-4" data-testid="change-password-card">
      <div className="flex items-baseline justify-between">
        <h4 className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">Change password</h4>
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white"
          data-testid="change-password-toggle-visibility"
        >
          {show ? "Hide" : "Show"}
        </button>
      </div>
      <p className="text-xs text-white/50 font-condensed">
        Minimum 12 characters. Your session on other devices will stay valid until they expire.
      </p>
      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-white/50 block mb-1">Current password</label>
          <input
            type={show ? "text" : "password"}
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="input-minimal"
            required
            data-testid="change-password-current"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-white/50 block mb-1">New password</label>
          <input
            type={show ? "text" : "password"}
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="input-minimal"
            required
            minLength={12}
            data-testid="change-password-new"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-white/50 block mb-1">Confirm new password</label>
          <input
            type={show ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="input-minimal"
            required
            minLength={12}
            data-testid="change-password-confirm"
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={busy || !current || !next || !confirm}
        className="pm-btn w-full disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="change-password-submit"
      >
        {busy ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}
